import React, { useMemo } from 'react';
import { Box, useTheme } from '@mui/material';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
  ChartEvent,
  ActiveElement,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import type { SummaryRow } from '../analyticsTypes';
import { categoricalSeriesPalette } from '../utils/chartPalette';

import { opsColors } from '../../../../theme/opsTokens';

ChartJS.register(ArcElement, ChartTooltip, Legend);

interface CategoryDonutChartProps {
  rows: SummaryRow[];
  onSliceClick?: (category: string) => void;
  height?: number;
}

const CategoryDonutChart: React.FC<CategoryDonutChartProps> = ({
  rows,
  onSliceClick,
  height = 240,
}) => {
  const { t } = useTranslation('admin');
  const theme = useTheme();
  const buckets = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of rows) {
      const key = r.category || 'uncategorized';
      map.set(key, (map.get(key) ?? 0) + r.count);
    }
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
  }, [rows]);

  const palette = useMemo(() => categoricalSeriesPalette(theme), [theme]);

  const data = useMemo(() => ({
    labels: buckets.map(([k]) => k),
    datasets: [
      {
        data: buckets.map(([, v]) => v),
        backgroundColor: buckets.map((_, i) => palette[i % palette.length]),
        borderColor: opsColors.neutral.panel,
        borderWidth: 2,
      },
    ],
  }), [buckets, palette, opsColors.neutral.panel]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    cutout: '60%',
    plugins: {
      legend: { display: true, position: 'right' as const, labels: { boxWidth: 12, font: { size: 12 } } },
      tooltip: { mode: 'index' as const, intersect: false },
    },
    onClick: onSliceClick
      ? (_e: ChartEvent, elements: ActiveElement[]) => {
          if (elements.length === 0) return;
          const idx = elements[0].index;
          const bucket = buckets[idx];
          if (bucket) onSliceClick(bucket[0]);
        }
      : undefined,
    onHover: onSliceClick
      ? (event: ChartEvent, elements: ActiveElement[]) => {
          const canvas = (event.native as MouseEvent | null)?.target as HTMLElement | null;
          if (canvas) canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default';
        }
      : undefined,
  }), [onSliceClick, buckets]);

  return (
    <Box sx={{ height }} role="img" aria-label={t('analytics.charts.donutAria', { count: buckets.length })}>
      <Doughnut data={data} options={options} />
    </Box>
  );
};

export default React.memo(CategoryDonutChart);
